import { BarList, Card, CardBody, CardHeader, EmptyState, LineChart, SegmentedButtonGroup, Stack, StatTile, Text } from '@olytic/foundation';

import { formatCurrency } from '../objects/format';
import type { QuoteBuilder } from './quotesApi';
import { roiModel, type ValueModel } from '@quote-math/valueModel';

const money = (n: number) => formatCurrency(Math.round(n));
const round1k = (n: number) => Math.round(n / 1000) * 1000;
const one1 = (n: number) => Math.round(n * 10) / 10;
const monthLabel = (m: number) => (m === 0 ? 'Start' : `Mo ${m}`);

/** The monthly cost the ROI counts: for a change order, only what it adds to today's bill. */
export function monthlyCostOf(data: QuoteBuilder): number {
  const { monthly } = data.totals;
  return data.quote.kind === 'change_order' ? monthly.after - monthly.current : monthly.after;
}

/** The ROI's opening lines, in the proposal builder's words; the bold part first. */
export function roiLead(model: ValueModel, roi: ReturnType<typeof roiModel>, monthlyCost: number): { bold: string; rest: string }[] {
  const license = monthlyCost * 12;
  const lines: { bold: string; rest: string }[] = [];
  if (roi.payback !== null) {
    lines.push({ bold: roi.payback < 12 ? 'Pays for itself in under 12 months' : `Pays for itself in about ${Math.round(roi.payback)} months`, rest: ', including the build.' });
  }
  lines.push({
    bold: `About ${money(round1k(roi.runNet))} a year in net return once live`,
    rest: license > 0 ? `, roughly ${Math.round(roi.totals.gross / license)}x the cost of the license.` : '.',
  });
  lines.push({ bold: `About ${money(round1k(roi.net))} of net return over ${model.horizonYears} years.`, rest: '' });
  if (model.ebitdaMultiple) {
    lines.push({
      bold: `About ${money(Math.round((roi.runNet * model.ebitdaMultiple) / 10000) * 10000)} of added enterprise value`,
      rest: ` at ${model.ebitdaMultiple}x EBITDA${model.ebitdaNote ? `, ${model.ebitdaNote}` : ''}.`,
    });
  }
  return lines;
}

const tile = 'rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)]';

/**
 * ROI: the economic case at the price on the order, using the value model and
 * the timeline, laid out as the proposal builder had it: the lead, four
 * figures, the cumulative curve with launch and break-even, the benefit by
 * driver, and the benefits we can't price.
 */
export function RoiTab({ data, model, onSave }: { data: QuoteBuilder; model: ValueModel; onSave: (model: ValueModel) => void }): JSX.Element {
  const oneTime = data.totals.oneTime.net;
  const monthlyCost = monthlyCostOf(data);
  const roi = roiModel(model, oneTime, monthlyCost);
  if (!roi.totals.gross) {
    return (
      <Card>
        <CardBody>
          <EmptyState title="No value model yet" description="Add metrics and the improvement to expect on the Value tab, and the return shows here." />
        </CardBody>
      </Card>
    );
  }
  const years = model.horizonYears;
  const license = monthlyCost * 12;
  const ev = model.ebitdaMultiple ? roi.runNet * model.ebitdaMultiple : null;
  const launch = Math.round(roi.goLive);
  const breakEven = roi.payback !== null && roi.payback <= roi.months ? Math.round(roi.payback) : null;

  return (
    <Stack>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <Text tone="muted">The economic case at the price on the order, using the value model and the project timeline. Built to show the customer.</Text>
        <SegmentedButtonGroup
          label="Horizon"
          size="sm"
          options={[{ value: '3', label: '3 years' }, { value: '5', label: '5 years' }]}
          value={String(years)}
          onChange={(v) => onSave({ ...model, horizonYears: Number(v) })}
        />
      </div>
      <ul className="flex flex-col gap-1.5 text-base">
        {roiLead(model, roi, monthlyCost).map((l) => (
          <li key={l.bold}>
            <span className="font-strong">{l.bold}</span>
            {l.rest}
          </li>
        ))}
      </ul>
      <div className="grid gap-2.5 md:grid-cols-2 lg:grid-cols-4">
        <StatTile className={tile} label="Payback" value={roi.payback === null ? `Beyond ${years} years` : `${one1(roi.payback)} months`} note="From kickoff, including the build" />
        <StatTile className={tile} label="Net return each year once live" value={money(roi.runNet)} note={license > 0 ? `Benefits are ${one1(roi.totals.gross / license)}x the yearly license` : 'Benefits at full run rate'} />
        <StatTile className={tile} label={`${years}-year net return`} value={money(roi.net)} note={`${one1(roi.multiple)}x return on cost`} />
        <StatTile className={tile} label="Added enterprise value" value={ev ? money(ev) : '—'} note={ev ? `At ${model.ebitdaMultiple}x EBITDA` : 'Set a multiple on the Value tab'} />
      </div>
      <div className="grid gap-3.5 lg:grid-cols-2">
        <LineChart
          title="Cumulative net value"
          description={`At the price on the order. Launch in month ${one1(roi.goLive)}, full benefit ${model.ramp} months after.`}
          data={roi.cumulative.map((net, m) => ({ month: monthLabel(m), net: Math.round(net) }))}
          config={{ net: { label: 'Cumulative net' } }}
          xKey="month"
          series={['net']}
          valueFormatter={(v) => formatCurrency(v)}
          height={280}
          referenceLines={launch > 0 ? [{ x: monthLabel(launch), label: 'Launch' }] : []}
          markers={breakEven !== null ? [{ x: monthLabel(breakEven), y: Math.round(roi.cumulative[breakEven] ?? 0), label: `Break-even · month ${one1(roi.payback!)}` }] : []}
        />
        <Card>
          <CardHeader title="Annual benefit by driver" />
          <CardBody>
            <Text tone="muted" className="mb-3">At full run rate.</Text>
            <BarList
              items={roi.totals.byDriver.map((d) => ({ key: d.label, label: d.label, note: d.type, value: Math.round(d.value) }))}
              valueFormatter={(v) => formatCurrency(v)}
            />
          </CardBody>
        </Card>
      </div>
      {model.quals.length ? (
        <Card>
          <CardHeader title="Beyond the numbers" />
          <CardBody>
            <div className="grid gap-2.5 md:grid-cols-2 lg:grid-cols-5">
              {model.quals.map((q) => (
                <div key={q.id} className="rounded-[var(--radius-lg)] border border-[var(--border)] px-3 py-2.5">
                  <div className="text-[13px] font-emphasis text-[var(--fg)]">{q.benefit}</div>
                  <div className="text-[12.5px] text-[var(--fg-muted)]">{q.detail}</div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      ) : null}
    </Stack>
  );
}
